export function initOrderFilters() {
    document.querySelectorAll<HTMLFormElement>('.js-order-filters-form').forEach($form => {
        new OrderFilters($form);
    });
}

export class OrderFilters {
    $form: HTMLFormElement;
    $filtersRowsHolder: HTMLElement|null; // Row holder
    $addFiltersRowButton: HTMLButtonElement|null; // Button to add a new row
    $resetButtons: NodeListOf<HTMLElement>; // Form reset buttons
    $numberContainer: HTMLElement|null; // Element containing the number of requested filters
    conjunction = 'and';
    $fieldTemplates: NodeListOf<HTMLElement>; // Form fields templates

    constructor($form: HTMLFormElement) {
        this.$form = $form;
        this.$filtersRowsHolder = this.$form.querySelector<HTMLElement>('.js-filters-rows-holder');
        this.$addFiltersRowButton = this.$form.querySelector<HTMLButtonElement>('.js-add-filters-row');
        this.$resetButtons = document.querySelectorAll<HTMLElement>('.js-order-filters-reset-button');
        this.$numberContainer = document.querySelector<HTMLElement>('.js-order-filters-number');
        this.$fieldTemplates = this.$form.querySelectorAll<HTMLElement>('.js-field-template');

        // Change the tab_filter input name
        const $tabFilterInput = this.$form.querySelector<HTMLInputElement>('.js-tab-filter-input');
        $tabFilterInput?.setAttribute('name', 'tab_filter');

        // Init rows already existing in the DOM
        this.$form.querySelectorAll<HTMLElement>('.js-filters-row').forEach($filtersRow => {
            this.initFiltersRow($filtersRow);
        });

        // If the form is already submitted, prefill all rows with sended values
        this.prefillFiltersRows();

        // Add a new row when the addFiltersRowButton is clicked
        this.$addFiltersRowButton?.addEventListener('click', () => {
            this.addFiltersRow();
        });
    }

    /**
     * Add a new filters row
     * 
     * @return {HTMLElement} The new row
     */
    addFiltersRow() : HTMLElement {
        // Create the row container
        const $item = document.createElement('div');
        $item.classList.add('row', 'js-filters-row');

        if (!this.$filtersRowsHolder) {
            return $item;
        }

        // Populate the row container
        const rowPrototype = this.$filtersRowsHolder.dataset.rowPrototype || '';
        const conjunctionPrototype = this.$filtersRowsHolder.dataset.conjunctionPrototype;
        const deleteButtonPrototype = this.$filtersRowsHolder.dataset.deleteButtonPrototype;

        const index = this.$filtersRowsHolder.dataset.index || '0';
        $item.innerHTML = conjunctionPrototype + rowPrototype.replace(/__name__/g, index) + deleteButtonPrototype;

        $item.dataset.index = index;

        this.initFiltersRow($item, true);

        // Add the new row into the rows holder
        this.$filtersRowsHolder.appendChild($item);
        this.$filtersRowsHolder.dataset.index = '' + (parseInt(index) + 1);

        return $item;
    }

    /**
     * Delete a filters row
     * 
     * @param {HTMLElement} $filtersRow 
     * 
     * @return {void}
     */
    deleteFiltersRow($filtersRow: HTMLElement) : void {
        if (!this.$filtersRowsHolder) {
            return;
        }

        const index = Array.from(this.$filtersRowsHolder.children).indexOf($filtersRow);

        // If removing the row containing the conjunction select and having a row after,
        // Move the conjunction select to the next row
        if (index === 1 && this.$filtersRowsHolder.children.length > 2) {
            const $newFiltersRowAtIndex1 = this.$filtersRowsHolder.children[2];
            const $conjunctionHolder = $newFiltersRowAtIndex1.querySelector<HTMLElement>('.js-conjunction-other');
            if (!$conjunctionHolder) {
                return;
            }

            $conjunctionHolder.innerHTML = $filtersRow.querySelector<HTMLElement>('.js-conjunction-second')?.innerHTML || '';
            $conjunctionHolder.classList.remove('js-conjunction-other');
            $conjunctionHolder.classList.add('js-conjunction-second');

            const $conjunctionSelect = $conjunctionHolder.querySelector<HTMLSelectElement>('select');
            if (!$conjunctionSelect) {
                return;
            }

            $conjunctionSelect.value = (Array.from($conjunctionSelect.children) as HTMLOptionElement[])
                .filter($opt => $opt.innerText === this.conjunction)
                .pop()
                ?.value || '';

            this.initConjunctionEvent($conjunctionSelect);
        }

        $filtersRow.remove();

        // If removing the first row
        if (index === 0) {
            if (!this.$filtersRowsHolder.children.length) {
                // If there is no row in rows holder, adding a new one
                this.addFiltersRow();
            } else {
                // If there is another rows in rows holder, change the conjunctions of first and second one
                const $rowAtIndex0 = (this.$filtersRowsHolder.children[0] as HTMLElement);
                const $conjunctionOfRow0 = $rowAtIndex0.querySelector('[data-for=conjunction]');

                $conjunctionOfRow0?.insertAdjacentHTML('afterend', this.$filtersRowsHolder.dataset.conjunctionPrototype || '');
                $conjunctionOfRow0?.remove();

                // Keep only the first conjunction
                $rowAtIndex0.querySelector<HTMLElement>('.js-conjunction-second')?.remove();
                $rowAtIndex0.querySelector<HTMLElement>('.js-conjunction-other')?.remove();
                
                const $rowAtIndex1 = (this.$filtersRowsHolder.children[1] as HTMLElement|undefined);
                if ($rowAtIndex1) {
                    const $conjunctionOfRow1 = $rowAtIndex1.querySelector('[data-for=conjunction]');

                    $conjunctionOfRow1?.insertAdjacentHTML('afterend', this.$filtersRowsHolder.dataset.conjunctionPrototype || '');
                    $conjunctionOfRow1?.remove();

                    // Keep only the second conjunction
                    $rowAtIndex1.querySelector<HTMLElement>('.js-conjunction-first')?.remove();
                    $rowAtIndex1.querySelector<HTMLElement>('.js-conjunction-other')?.remove();

                    // Populate the select with the right value
                    const $conjunctionSelect = $rowAtIndex1.querySelector('.js-conjunction-second select') as HTMLSelectElement;
                    $conjunctionSelect.value = (Array.from($conjunctionSelect.children) as HTMLOptionElement[])
                        .filter($opt => $opt.innerText === this.conjunction)
                        .pop()
                        ?.value || '';
        
                    // Init the select event
                    this.initConjunctionEvent($conjunctionSelect);
                }
            }
        }
    }

    /**
     * Initialize the filters row events
     * 
     * @param {HTMLElement} $filtersRow
     * @param {boolean}     isNew If this is a new element not already added in the DOM
     * 
     * @return {void}
     */
    initFiltersRow($filtersRow: HTMLElement, isNew: boolean = false) : void {
        if (!this.$filtersRowsHolder) {
            return;
        }

        const index = isNew ? this.$filtersRowsHolder.children.length : Array.from(this.$filtersRowsHolder.children).indexOf($filtersRow);

        // Remove the unwanted conjunctions
        const $conjunctionFirst = $filtersRow.querySelector<HTMLElement>('.js-conjunction-first');
        const $conjunctionSecond = $filtersRow.querySelector<HTMLElement>('.js-conjunction-second');
        const $conjunctionOther = $filtersRow.querySelector<HTMLElement>('.js-conjunction-other');

        if (index === 0) {
            $conjunctionSecond?.remove();
            $conjunctionOther?.remove();
            // $filtersRow.querySelector<HTMLElement>('.js-remove-filters-row')?.remove();
        } else if (index === 1) {
            $conjunctionFirst?.remove();
            $conjunctionOther?.remove();

            this.initConjunctionEvent($conjunctionSecond?.querySelector('select') as HTMLSelectElement);
        } else {
            $conjunctionFirst?.remove();
            $conjunctionSecond?.remove();

            ($conjunctionOther?.querySelector('input') as HTMLInputElement).value = this.conjunction;
        }

        this.onItemChange($filtersRow);
        $filtersRow.querySelector<HTMLSelectElement>('.js-item-container select')?.addEventListener('change', () => {
            this.onItemChange($filtersRow);
        });

        const $removeFiltersRowButton = $filtersRow.querySelector<HTMLButtonElement>('.js-remove-filters-row');

        // Remove the row when click on the delete button
        if ($removeFiltersRowButton) {
            $removeFiltersRowButton.addEventListener('click', () => {
                this.deleteFiltersRow($filtersRow);
            });
        }
    }

    /**
     * Initialize events relative to the conjunction select element
     * 
     * @param {HTMLSelectElement} $conjunctionSelect
     * 
     * @return {void}
     */
    initConjunctionEvent($conjunctionSelect: HTMLSelectElement) : void {
        this.conjunction = $conjunctionSelect.selectedOptions[0].innerText;

        $conjunctionSelect.addEventListener('change', () => {
            this.conjunction = $conjunctionSelect.selectedOptions[0].innerText;

            this.$filtersRowsHolder?.querySelectorAll<HTMLInputElement>('.js-conjunction-other input').forEach($conjunctionInput => {
                $conjunctionInput.value = this.conjunction;
            });
        });
    }

    /**
     * Event fire when an item is changed in a filter row
     * 
     * @param {HTMLElement} $filtersRow 
     * 
     * @return {void}
     */
    onItemChange($filtersRow: HTMLElement) : void {
        // Add the right value field
        const selectedItem = ($filtersRow.querySelector('.js-item-container select') as HTMLSelectElement).value;
        const $fieldTemplate = Array.from(this.$fieldTemplates).filter($template => $template.dataset.for === selectedItem).pop() as HTMLElement;
        const index = $filtersRow.dataset.index;

        const replaceKeyRegex = new RegExp(`\\[${selectedItem}\\]`, 'g');
        const replaceIdRegex = new RegExp(`_${selectedItem}`, 'g');

        const $valueContainer = $filtersRow.querySelector('.js-value-container') as HTMLElement;
        $valueContainer.dataset.for = selectedItem;

        $valueContainer.innerHTML = $fieldTemplate.innerHTML
            .replace(replaceKeyRegex, `[row][${index}][${selectedItem}]`)
            .replace(replaceIdRegex, `_row_${index}_${selectedItem}`);

        // Add the right operators
        const $operatorField = $filtersRow.querySelector('.js-operator-container select') as HTMLSelectElement;
        $operatorField.innerHTML = '';

        type OperatorType = {
            name: string;
            label: string;
            types: string[];
        };

        const operators: OperatorType[] = JSON.parse($valueContainer.querySelector<HTMLElement>('[data-operators]')?.dataset.operators || '{}');
        operators.forEach(operator => {
            const $option = document.createElement('option');
            $option.setAttribute('value', operator.name);
            $option.innerText = operator.label;

            $operatorField.appendChild($option);
        });

        // Add the "contains-double' class to the row is containing a value with 2 fields
        const hasDoubleFieldsValue = operators.map(operator => operator.name).filter(name => ['date_in', 'date_not_in'].includes(name)).length > 0;
        $filtersRow.classList.toggle('contains-double', hasDoubleFieldsValue);
    }

    /**
     * Prefille the filters rows on page loading
     * 
     * @return {void}
     */
    prefillFiltersRows() : void {
        type RowType = {
            item: string;
            operator: string;
            service?: string;
            begin?: string;
            end?: string;
        };

        const rows: RowType[] = [];

        const parameters = new URLSearchParams(location.search);
        const parametersKey = Array.from(parameters.keys());

        let conjunctionValue: string|null = null;

        parametersKey.forEach(key => {
            if (/conjunction/.test(key)) {
                conjunctionValue = parameters.get(key);
            }

            if (!/\d/.test(key)) {
                return;
            }

            const rowIndex = (key.match(/\d+/) as string[]|number[])[0] as number;
            const name = (key.match(new RegExp(/\[(\w+)\]$/)) as string[])[1] as keyof RowType;

            const row = rows[rowIndex] || {};
            row[name] = parameters.get(key) || '';

            rows[rowIndex] = row;
        });

        let nbFilters = 0;

        rows.filter(r => r).forEach((rowDetails, index) => {
            // If value is empty, skip
            if (
                (typeof rowDetails.begin !== 'undefined' && (!rowDetails.begin && !rowDetails.end)) ||
                (typeof rowDetails[rowDetails.item as keyof RowType] !== 'undefined' && !rowDetails[rowDetails.item as keyof RowType])
            ) {
                return;
            }

            const $row = this.$form.querySelector(`.js-filters-row:nth-child(${index + 1})`) || this.addFiltersRow();
            const $itemSelect = $row.querySelector('.js-item-container select') as HTMLSelectElement;
            $itemSelect.value = rowDetails.item;
            $itemSelect.dispatchEvent(new Event('change'));

            ($row.querySelector('.js-operator-container select') as HTMLSelectElement).value = rowDetails.operator;
            if (typeof rowDetails.begin !== 'undefined' || typeof rowDetails.end !== 'undefined') {
                ($row.querySelectorAll('.js-value-container input')[0] as HTMLInputElement).value = rowDetails.begin || '';
                ($row.querySelectorAll('.js-value-container input')[1] as HTMLInputElement).value = rowDetails.end || '';
            } else {
                ($row.querySelector('.js-value-container input, .js-value-container select') as HTMLSelectElement).value = rowDetails[rowDetails.item as keyof RowType] || '';
            }

            nbFilters++;
        });

        // Set the conjunction
        if (conjunctionValue) {
            const $conjunctionSelect = this.$form.querySelector<HTMLInputElement>('.js-filters-row-conjunction');
            if ($conjunctionSelect) {
                $conjunctionSelect.value = conjunctionValue;
                $conjunctionSelect.dispatchEvent(new Event('change'));
            }
        }

        if (nbFilters) {
            // Write the number of selected filters
            if (this.$numberContainer) {
                this.$numberContainer.innerText = nbFilters + '';
                this.$numberContainer.classList.remove('hidden');
            }

            // Show the reset buttons
            this.$resetButtons.forEach($resetButton => {
                $resetButton.classList.remove('hidden');
            });
        }
    }
}
